import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { packsApi, type OneShotPack, type PackSimulatorOption } from "../lib/api";
import { pickSimulatorDescription } from "../lib/simulator-description-i18n";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Check } from "lucide-react";

/**
 * Configuration du périmètre simulateur des packs one-shot STARTER/FOCUS du compte.
 * Affiché dans Mon compte (clients existants) et à l'onboarding (nouveaux clients).
 * Ne rend RIEN s'il n'y a aucun pack à configurer (PREMIUM = aucun choix requis).
 * cf. spec/packs-sessions-unifies.md §5.2
 */
/** onAllConfigured : appelé quand plus aucun pack ne nécessite de configuration
 *  (utilisé par l'onboarding pour avancer à l'étape suivante). */
export function OneShotPackConfig({ onAllConfigured }: { onAllConfigured?: () => void } = {}) {
  const { t, i18n } = useTranslation();
  const [packs, setPacks] = useState<OneShotPack[]>([]);
  const [sims, setSims] = useState<PackSimulatorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<OneShotPack[]> => {
    try {
      const data = await packsApi.getOneShot();
      setPacks(data.packs);
      setSims(data.availableSimulators);
      const init: Record<string, string[]> = {};
      for (const p of data.packs) init[p.id] = p.simulatorIds;
      setSelection(init);
      return data.packs;
    } catch {
      /* silencieux : section masquée si erreur */
      return [];
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toConfigure = packs.filter((p) => p.needsConfig);
  if (loading || toConfigure.length === 0) return null;

  const toggle = (grantId: string, simId: string, max: number) => {
    setSelection((prev) => {
      const cur = prev[grantId] ?? [];
      if (cur.includes(simId)) return { ...prev, [grantId]: cur.filter((s) => s !== simId) };
      if (cur.length >= max) return prev; // max atteint
      return { ...prev, [grantId]: [...cur, simId] };
    });
  };

  const save = async (pack: OneShotPack) => {
    const chosen = selection[pack.id] ?? [];
    if (chosen.length === 0) {
      setError(t("packs.chooseAtLeastOne"));
      return;
    }
    setSaving(pack.id);
    setError(null);
    try {
      await packsApi.setSimulators(pack.id, chosen);
      const fresh = await load(); // le pack configuré sort de la liste
      if (onAllConfigured && fresh.filter((p) => p.needsConfig).length === 0) {
        onAllConfigured();
      }
    } catch (e: any) {
      setError(e?.message || t("packs.saveError"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-3">
      {toConfigure.map((pack) => {
        const max = pack.maxSimulators ?? 1;
        const chosen = selection[pack.id] ?? [];
        return (
          <Card key={pack.id} className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 space-y-3">
              <div>
                <p className="font-semibold" style={{ color: "#384872" }}>
                  {t("packs.configureTitle", { name: pack.licenceName })}
                </p>
                <p className="text-sm text-muted-foreground">{t("packs.configureHint", { count: max })}</p>
                <p className="text-xs text-muted-foreground">{t("account.coachesIncluded")}</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {sims.map((s) => {
                  const sel = chosen.includes(s.id);
                  const disabled = !sel && chosen.length >= max;
                  const desc = pickSimulatorDescription(s, i18n.language);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggle(pack.id, s.id, max)}
                      className={`relative text-left p-3 rounded-lg border transition ${
                        sel
                          ? "border-[#988EF2] ring-2 ring-[#988EF2]/30 bg-white"
                          : disabled
                            ? "opacity-40 border-slate-200 bg-white"
                            : "border-slate-200 bg-white hover:border-[#988EF2]"
                      }`}
                    >
                      {sel && (
                        <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-[#988EF2] flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </span>
                      )}
                      <p className="font-semibold text-sm pr-6" style={{ color: "#384872" }}>{s.name}</p>
                      {desc && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{desc}</p>}
                    </button>
                  );
                })}
              </div>
              {error && (
                <p className="text-sm" style={{ color: "#C0423F" }}>
                  {error}
                </p>
              )}
              <Button
                onClick={() => save(pack)}
                disabled={saving === pack.id || chosen.length === 0}
                style={{ backgroundColor: "#F99002" }}
              >
                {saving === pack.id ? t("packs.saving") : t("packs.confirm", { count: chosen.length, max })}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
